import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  AlertTriangle, ShieldAlert, Clock, CheckCircle, 
  RefreshCw, ChevronDown, ChevronUp, User
} from 'lucide-react';
import { toast } from 'sonner';

import API from '@/config/api';
// API imported from config;

const CertificationAlertsWidget = ({ getAuthHeaders }) => {
  const [expiringCerts, setExpiringCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [timeframe, setTimeframe] = useState(90); // days

  const fetchExpiringCerts = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/staff/certifications/expiring?days=${timeframe}`,
        getAuthHeaders()
      );
      setExpiringCerts(res.data.certifications || []);
    } catch (error) {
      console.error('Failed to load expiring certifications');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, timeframe]);

  useEffect(() => {
    fetchExpiringCerts();
  }, [fetchExpiringCerts]);

  const handleCheckAndAlert = async () => {
    setChecking(true);
    try {
      const res = await axios.post(
        `${API}/staff/certifications/check-and-alert`,
        {},
        getAuthHeaders()
      );
      toast.success(res.data.message);
      fetchExpiringCerts();
    } catch (error) {
      toast.error('Failed to check certifications');
    } finally {
      setChecking(false);
    }
  };

  const getStatusBadge = (cert) => {
    if (cert.status === 'expired') {
      return (
        <Badge variant="destructive" className="text-xs">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Expired
        </Badge>
      );
    }
    if (cert.days_until_expiration <= 7) {
      return (
        <Badge className="bg-red-500 text-xs">
          <Clock className="w-3 h-3 mr-1" />
          {cert.days_until_expiration} days
        </Badge>
      );
    }
    if (cert.days_until_expiration <= 30) {
      return (
        <Badge className="bg-amber-500 text-xs">
          <Clock className="w-3 h-3 mr-1" />
          {cert.days_until_expiration} days
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-xs">
        <Clock className="w-3 h-3 mr-1" />
        {cert.days_until_expiration} days
      </Badge>
    );
  };

  const expiredCount = expiringCerts.filter(c => c.status === 'expired').length;
  const criticalCount = expiringCerts.filter(c => c.status !== 'expired' && c.days_until_expiration <= 7).length;
  const warningCount = expiringCerts.filter(c => c.status !== 'expired' && c.days_until_expiration > 7 && c.days_until_expiration <= 30).length;

  if (loading) {
    return (
      <Card data-testid="certification-alerts-widget">
        <CardContent className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="certification-alerts-widget" className={expiringCerts.length > 0 ? 'border-amber-300' : ''}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldAlert className={`w-5 h-5 ${expiringCerts.length > 0 ? 'text-amber-500' : 'text-green-500'}`} />
            <div>
              <CardTitle className="text-lg">Staff Certifications</CardTitle>
              <CardDescription>
                {expiringCerts.length > 0 
                  ? `${expiringCerts.length} certification${expiringCerts.length !== 1 ? 's' : ''} need attention`
                  : 'All certifications are up to date'
                }
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleCheckAndAlert}
              disabled={checking}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${checking ? 'animate-spin' : ''}`} />
              Check
            </Button>
            {expiringCerts.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            )}
          </div>
        </div>
        
        {/* Summary Badges */}
        {expiringCerts.length > 0 && (
          <div className="flex gap-2 mt-2">
            {expiredCount > 0 && (
              <Badge variant="destructive" className="text-xs">
                {expiredCount} Expired
              </Badge>
            )}
            {criticalCount > 0 && (
              <Badge className="bg-red-500 text-xs">
                {criticalCount} Critical (≤7 days)
              </Badge>
            )}
            {warningCount > 0 && (
              <Badge className="bg-amber-500 text-xs">
                {warningCount} Warning (≤30 days)
              </Badge>
            )}
          </div>
        )}
      </CardHeader>
      
      {expanded && expiringCerts.length > 0 && (
        <CardContent className="pt-0">
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {expiringCerts.map((cert, idx) => (
              <div 
                key={idx}
                className={`flex items-center justify-between p-2 rounded-lg border ${
                  cert.status === 'expired' 
                    ? 'bg-red-50 border-red-200' 
                    : cert.days_until_expiration <= 7
                      ? 'bg-red-50/50 border-red-100'
                      : cert.days_until_expiration <= 30
                        ? 'bg-amber-50 border-amber-200'
                        : 'bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                    <User className="w-4 h-4 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{cert.staff_name}</p>
                    <p className="text-xs text-muted-foreground">{cert.certification_name}</p>
                    <p className="text-xs text-muted-foreground">
                      Expires: {new Date(cert.expiration_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                {getStatusBadge(cert)}
              </div>
            ))}
          </div>
          
          <div className="mt-3 pt-3 border-t">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Showing next {timeframe} days</span>
              <div className="flex gap-1">
                {[30, 60, 90].map((days) => (
                  <Button 
                    key={days}
                    variant={timeframe === days ? 'default' : 'ghost'}
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => setTimeframe(days)}
                  >
                    {days}d
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      )}
      
      {expiringCerts.length === 0 && (
        <CardContent className="pt-0">
          <div className="flex items-center justify-center gap-2 text-green-600 py-4">
            <CheckCircle className="w-5 h-5" />
            <span>All staff certifications are current</span>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default CertificationAlertsWidget;
